import { IIssueFaultTypeResponse, IssueHardwareType } from '@kcftech/prometheus-client';
import { keepPreviousData } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { DefaultIssueMode, IssueMode } from 'pages/Desk/issues/shared';
import { useCallback, useMemo } from 'react';
import { createTree } from 'shared/data_tree/tree';
import { Tree, TreeNodeData } from 'shared/data_tree/types';
import { sortBy } from 'utils/array';
import { Filter, useQueryAll } from 'utils/query_v3';

export type IssueFaultType = Readonly<{
    id: string;
    name: string;
    fullName: string;
    internalOnly: boolean;
}>;

const sortTreeData = (treeData: TreeNodeData<IssueFaultType>[]) =>
    sortBy(treeData, (data) => data.data.name);

type BuildTreeDataOptions = Readonly<{
    faultType: IIssueFaultTypeResponse;
    parentFaultType: IssueFaultType | undefined;
    faultTypes: IIssueFaultTypeResponse[];
}>;

const buildTreeData = ({
    faultType,
    parentFaultType,
    faultTypes,
}: BuildTreeDataOptions): TreeNodeData<IssueFaultType> => {
    const issueFaultType: IssueFaultType = {
        id: faultType.Id,
        name: faultType.Name,
        fullName: parentFaultType
            ? `${parentFaultType.fullName} ${faultType.Name}`
            : faultType.Name,
        internalOnly: faultType.InternalOnly,
    };

    const children = faultTypes
        .filter((type) => type.ParentId === faultType.Id)
        .map((child) =>
            buildTreeData({
                faultType: child,
                parentFaultType: issueFaultType,
                faultTypes,
            }),
        );

    return { id: faultType.Id, data: issueFaultType, children: sortTreeData(children) };
};

const buildTree = (faultTypes: IIssueFaultTypeResponse[]) => {
    const rootData: TreeNodeData<IssueFaultType>[] = faultTypes
        .filter((type) => !type.ParentId)
        .map((type) =>
            buildTreeData({ faultType: type, parentFaultType: undefined, faultTypes }),
        );

    return createTree<IssueFaultType>(sortTreeData(rootData));
};

export type UseFaultTypesResult = Readonly<{
    isLoading: boolean;
    faultTypesTree: Tree<IssueFaultType> | undefined;
    getFaultType: (faultTypeId: string) => IssueFaultType | undefined;
}>;

export type UseFaultTypesProps = Readonly<{
    mode?: IssueMode;
    hardwareType?: IssueHardwareType;
    isInternalOnly?: boolean;
    forManualCreation?: boolean;
}>;

export function useFaultTypes({
    mode = DefaultIssueMode,
    hardwareType,
    isInternalOnly,
    forManualCreation,
}: UseFaultTypesProps): UseFaultTypesResult {
    if (mode === 'machineHealth' && hardwareType !== undefined) {
        throw new Error(
            'Hardware Type is not a filterable property for Machine Health issues',
        );
    }
    const { data: faultTypes, isLoading } = useQueryAll(
        ['issue-fault-types', mode, hardwareType, isInternalOnly, forManualCreation],
        (page) => {
            const filter = Filter.and(
                hardwareType !== undefined
                    ? Filter.eq('HardwareType', hardwareType)
                    : undefined,
                forManualCreation !== undefined && mode === 'hardwareHealth'
                    ? Filter.or(
                          Filter.eq('ForManualCreation', forManualCreation),
                          // We need to include the parent fault type to allow the tree to show
                          Filter.isNull('ParentId'),
                      )
                    : undefined,
                isInternalOnly !== undefined
                    ? Filter.eq('InternalOnly', isInternalOnly)
                    : undefined,
            );

            return mode === 'machineHealth'
                ? sdClient.getIssueFaultTypes({ page, pageLimit: 1000, filter })
                : sdClient.getIssueHardwareHealthFaultTypes({
                      page,
                      pageLimit: 1000,
                      filter,
                  });
        },
        {
            placeholderData: keepPreviousData,
        },
    );

    const faultTypesTree = useMemo(
        () => (faultTypes ? buildTree(faultTypes) : undefined),
        [faultTypes],
    );

    const getFaultType = useCallback<UseFaultTypesResult['getFaultType']>(
        (faultTypeId: string) =>
            faultTypes && faultTypesTree?.has(faultTypeId)
                ? faultTypesTree.get(faultTypeId).data
                : undefined,
        [faultTypes, faultTypesTree],
    );

    return {
        isLoading,
        faultTypesTree,
        getFaultType,
    };
}

export function filterFaultTypes(
    faultTypesTree: Tree<IssueFaultType>,
    filterText: string,
): Tree<IssueFaultType> {
    if (!filterText) {
        return faultTypesTree;
    }

    const nodeIdsToKeep = faultTypesTree.allNodes
        .filter((node) => node.data.fullName.toLowerCase().includes(filterText))
        .flatMap((node) => [node, ...node.hierarchy])
        .map((node) => node.id);

    const nodeIdsToRemove = faultTypesTree.allNodes
        .filter((node) => !nodeIdsToKeep.includes(node.id))
        .map((node) => node.id);

    return faultTypesTree.remove(nodeIdsToRemove);
}
